import React, { useEffect, useRef, useState } from "react";
import {
  Send,
  Plus,
  Trash2,
  ToggleLeft,
  ToggleRight,
  PlayCircle,
  Edit3,
  X,
  Clock,
  CalendarDays,
  FlaskConical,
  CheckCircle2,
  Info,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import TextInput from "../../ui/TextInput";
import Dropdown from "../../ui/SelectDropdown";
import TextArea from "../../ui/TextArea";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import { RootState } from "../../../redux/app/store";
import {
  createScheduledNotification,
  fetchScheduledNotifications,
  updateScheduledNotification,
  deleteScheduledNotification,
  toggleScheduledNotification,
  sendScheduledNow,
  dryRunScheduledNotification,
  clearDryRunResult,
} from "../../../redux/features/notificationSlice";

const intervalTypeOptions = [
  { label: "Daily", value: "DAILY" },
  { label: "Weekly", value: "WEEKLY" },
  { label: "Monthly", value: "MONTHLY" },
  { label: "Random", value: "RANDOM" },
];

const targetUserTypeOptions = [
  { label: "Both (Vendor & Buyer)", value: "BOTH" },
  { label: "Vendor", value: "VENDOR" },
  { label: "Buyer", value: "BUYER" },
];

const notificationTypeOptions = [
  { label: "Paid Contract", value: "PAID_CONTRACT" },
  { label: "Courier Update", value: "COURIER_UPDATE" },
  { label: "Order Status Update", value: "ORDER_STATUS_UPDATE" },
  { label: "Dispute", value: "DISPUTE" },
  { label: "Chat Message", value: "CHAT_MESSAGE" },
  { label: "Buyer Request Created", value: "BUYER_REQUEST_CREATED" },
  { label: "Buyer Request Expired", value: "BUYER_REQUEST_EXPIRED" },
  { label: "Vendor Request Received", value: "VENDOR_REQUEST_RECEIVED" },
  { label: "Vendor Request Expired", value: "VENDOR_REQUEST_EXPIRED" },
  { label: "Contract Offer Received", value: "CONTRACT_OFFER_RECEIVED" },
  { label: "Product Approved", value: "PRODUCT_APPROVED" },
  { label: "Product Rejected", value: "PRODUCT_REJECTED" },
  { label: "Info", value: "INFO" },
  { label: "App Promotion", value: "APP_PROMOTION" },
  { label: "Promotional Offer", value: "PROMOTIONAL_OFFER" },
  { label: "Weekly Deal", value: "WEEKLY_DEAL" },
  { label: "Flash Sale", value: "FLASH_SALE" },
];

const daysOfWeekOptions = [
  { label: "Mon", value: 1 },
  { label: "Tue", value: 2 },
  { label: "Wed", value: 3 },
  { label: "Thu", value: 4 },
  { label: "Fri", value: 5 },
  { label: "Sat", value: 6 },
  { label: "Sun", value: 0 },
];

const initialFormState = {
  title: "",
  message: "",
  scheduleTime: "09:00",
  intervalType: "DAILY",
  intervalValue: 1,
  durationDays: 14,
  startDate: new Date().toISOString().split("T")[0],
  targetUserType: "BOTH",
  notificationType: "PROMOTIONAL_OFFER",
  daysOfWeek: [] as number[],
  dayOfMonth: 1,
};

const AutomatedMessages: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    scheduledNotifications,
    scheduledLoading,
    scheduledCreating,
    scheduledUpdating,
    scheduledDeleting,
    scheduledToggling,
    scheduledSendingNow,
    scheduledDryRunning,
    scheduledDryRunResult,
  } = useAppSelector((state: RootState) => state.notifications);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(initialFormState);
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarViewDate, setCalendarViewDate] = useState(() => {
    const d = new Date();
    return { month: d.getMonth(), year: d.getFullYear() };
  });
  const calendarRef = useRef<HTMLDivElement>(null);

  // Close calendar on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(e.target as Node)
      ) {
        setShowCalendar(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [formErrors, setFormErrors] = useState<{
    title?: string;
    message?: string;
  }>({});

  useEffect(() => {
    dispatch(fetchScheduledNotifications());
  }, [dispatch]);

  const resetForm = () => {
    setForm(initialFormState);
    setFormErrors({});
    setEditingId(null);
    setShowForm(false);
    dispatch(clearDryRunResult());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: { title?: string; message?: string } = {};
    if (!form.title.trim()) errors.title = "Title is required";
    if (!form.message.trim()) errors.message = "Message is required";
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const payload: any = {
      title: form.title,
      message: form.message,
      scheduleTime: form.scheduleTime,
      intervalType: form.intervalType,
      intervalValue: Number(form.intervalValue),
      durationDays: Number(form.durationDays),
      startDate: form.startDate,
      targetUserType: form.targetUserType,
      notificationType: form.notificationType,
      daysOfWeek: form.daysOfWeek.length > 0 ? form.daysOfWeek : [],
      dayOfMonth: Number(form.dayOfMonth) || 1,
      specificUserIds: [],
    };

    try {
      if (editingId) {
        await dispatch(
          updateScheduledNotification({
            id: editingId,
            data: {
              title: payload.title,
              message: payload.message,
              scheduleTime: payload.scheduleTime,
              intervalType: payload.intervalType,
              intervalValue: payload.intervalValue,
              durationDays: payload.durationDays,
            },
          }),
        ).unwrap();
      } else {
        await dispatch(createScheduledNotification(payload)).unwrap();
      }
      resetForm();
      dispatch(fetchScheduledNotifications());
    } catch {
      // error handled by thunk
    }
  };

  const handleDryRun = async () => {
    const errors: { title?: string; message?: string } = {};
    if (!form.title.trim()) errors.title = "Title is required";
    if (!form.message.trim()) errors.message = "Message is required";
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;
    const payload: any = {
      title: form.title,
      message: form.message,
      scheduleTime: form.scheduleTime,
      intervalType: form.intervalType,
      intervalValue: Number(form.intervalValue),
      durationDays: Number(form.durationDays),
      startDate: form.startDate,
      targetUserType: form.targetUserType,
      notificationType: form.notificationType,
      daysOfWeek: form.daysOfWeek.length > 0 ? form.daysOfWeek : [],
      dayOfMonth: Number(form.dayOfMonth) || 1,
      specificUserIds: [],
    };
    await dispatch(dryRunScheduledNotification({ createDto: payload }));
  };

  const handleEdit = (item: any) => {
    setForm({
      title: item.title ?? "",
      message: item.message ?? "",
      scheduleTime: item.scheduleTime ?? "09:00",
      intervalType: item.intervalType ?? "DAILY",
      intervalValue: item.intervalValue ?? 1,
      durationDays: item.durationDays ?? 14,
      startDate: item.startDate
        ? new Date(item.startDate).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0],
      targetUserType: item.targetUserType ?? "ALL",
      notificationType: item.notificationType ?? "PROMOTIONAL_OFFER",
      daysOfWeek: item.daysOfWeek ?? [],
      dayOfMonth: item.dayOfMonth ?? 1,
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = (id: number) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this scheduled notification?",
      )
    )
      return;
    dispatch(deleteScheduledNotification(id));
  };

  const handleToggle = (id: number) => {
    dispatch(toggleScheduledNotification(id));
  };

  const handleSendNow = async (id: number) => {
    if (!window.confirm("Send this notification immediately?")) return;
    await dispatch(sendScheduledNow(id));
  };

  const toggleDayOfWeek = (day: number) => {
    setForm((prev) => ({
      ...prev,
      daysOfWeek: prev.daysOfWeek.includes(day)
        ? prev.daysOfWeek.filter((d) => d !== day)
        : [...prev.daysOfWeek, day],
    }));
  };

  const formatIntervalLabel = (item: any) => {
    const type = item.intervalType?.toLowerCase();
    const val = item.intervalValue ?? 1;
    if (type === "daily") return val === 1 ? "Every day" : `Every ${val} days`;
    if (type === "weekly") {
      const days = (item.daysOfWeek ?? [])
        .map((d: number) => daysOfWeekOptions.find((o) => o.value === d)?.label)
        .filter(Boolean)
        .join(", ");
      return days ? `Weekly on ${days}` : "Weekly";
    }
    if (type === "monthly") return `Monthly on day ${item.dayOfMonth ?? 1}`;
    if (type === "random") return "Random (system)";
    return type ?? "—";
  };

  const schedulesList = Array.isArray(scheduledNotifications)
    ? scheduledNotifications
    : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Automated Messages</h2>
        {!showForm && (
          <button
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 text-sm"
          >
            <Plus className="w-4 h-4" />
            New Schedule
          </button>
        )}
      </div>

      {/* ── Create / Edit Form ── */}
      {showForm && (
        <form
          onSubmit={handleSubmit}
          className="bg-white shadow-lg rounded-2xl p-6 space-y-4"
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-md font-semibold">
              {editingId
                ? "Edit Scheduled Message"
                : "Create Scheduled Message"}
            </h3>
            <button
              type="button"
              onClick={resetForm}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Title */}
          <div>
            <TextInput
              id="sched-title"
              type="text"
              placeholder="Notification Title"
              value={form.title}
              register={{
                onChange: (e: any) => {
                  setForm({ ...form, title: e.target.value });
                  if (formErrors.title)
                    setFormErrors((p) => ({ ...p, title: undefined }));
                },
              }}
            />
            {formErrors.title && (
              <p className="text-red-500 text-xs mt-1">{formErrors.title}</p>
            )}
          </div>

          {/* Message */}
          <div>
            <TextArea
              id="sched-message"
              placeholder="Notification Message"
              value={form.message}
              onChange={(e) => {
                setForm({ ...form, message: e.target.value });
                if (formErrors.message)
                  setFormErrors((p) => ({ ...p, message: undefined }));
              }}
            />
            {formErrors.message && (
              <p className="text-red-500 text-xs mt-1">{formErrors.message}</p>
            )}
          </div>

          {/* Schedule Time – custom picker */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Schedule Time
            </label>
            <div className="flex items-center gap-2">
              {/* Hour */}
              <select
                value={(() => {
                  const h = parseInt(form.scheduleTime.split(":")[0]) || 0;
                  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
                  return String(h12);
                })()}
                onChange={(e) => {
                  const h12 = parseInt(e.target.value);
                  const currentH =
                    parseInt(form.scheduleTime.split(":")[0]) || 0;
                  const mins = form.scheduleTime.split(":")[1] || "00";
                  const isPM = currentH >= 12;
                  let h24 = isPM
                    ? h12 === 12
                      ? 12
                      : h12 + 12
                    : h12 === 12
                      ? 0
                      : h12;
                  setForm({
                    ...form,
                    scheduleTime: `${String(h24).padStart(2, "0")}:${mins}`,
                  });
                }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-primary focus:border-primary bg-white appearance-none cursor-pointer"
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
                  <option key={h} value={h}>
                    {h}
                  </option>
                ))}
              </select>

              <span className="text-gray-500 font-semibold text-lg">:</span>

              {/* Minute */}
              <select
                value={form.scheduleTime.split(":")[1] || "00"}
                onChange={(e) => {
                  const hrs = form.scheduleTime.split(":")[0] || "09";
                  setForm({
                    ...form,
                    scheduleTime: `${hrs}:${e.target.value}`,
                  });
                }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-primary focus:border-primary bg-white appearance-none cursor-pointer"
              >
                {[
                  "00",
                  "01",
                  "02",
                  "03",
                  "04",
                  "05",
                  "06",
                  "07",
                  "08",
                  "09",
                  "10",
                  "11",
                  "12",
                  "13",
                  "14",
                  "15",
                  "16",
                  "17",
                  "18",
                  "19",
                  "20",
                  "21",
                  "22",
                  "23",
                  "24",
                  "25",
                  "26",
                  "27",
                  "28",
                  "29",
                  "30",
                  "31",
                  "32",
                  "33",
                  "34",
                  "35",
                  "36",
                  "37",
                  "38",
                  "39",
                  "40",
                  "41",
                  "42",
                  "43",
                  "44",
                  "45",
                  "46",
                  "47",
                  "48",
                  "49",
                  "50",
                  "51",
                  "52",
                  "53",
                  "54",
                  "55",
                  "56",
                  "57",
                  "58",
                  "59",
                ].map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>

              {/* AM / PM */}
              <select
                value={
                  parseInt(form.scheduleTime.split(":")[0]) >= 12 ? "PM" : "AM"
                }
                onChange={(e) => {
                  const currentH =
                    parseInt(form.scheduleTime.split(":")[0]) || 0;
                  const mins = form.scheduleTime.split(":")[1] || "00";
                  const currentH12 =
                    currentH === 0
                      ? 12
                      : currentH > 12
                        ? currentH - 12
                        : currentH;
                  const newIsPM = e.target.value === "PM";
                  let h24 = newIsPM
                    ? currentH12 === 12
                      ? 12
                      : currentH12 + 12
                    : currentH12 === 12
                      ? 0
                      : currentH12;
                  setForm({
                    ...form,
                    scheduleTime: `${String(h24).padStart(2, "0")}:${mins}`,
                  });
                }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-primary focus:border-primary bg-white font-medium cursor-pointer"
              >
                <option value="AM">AM</option>
                <option value="PM">PM</option>
              </select>

              <Clock className="w-4 h-4 text-gray-400 ml-1" />
            </div>
          </div>

          {/* Interval Type & Value row */}
          <div className="grid grid-cols-2 gap-4">
            <Dropdown
              label="Interval Type"
              placeholder="Select interval"
              options={intervalTypeOptions}
              value={form.intervalType}
              onSelect={(val) => setForm({ ...form, intervalType: val })}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Interval Value
              </label>
              <input
                type="number"
                min={1}
                value={form.intervalValue}
                onChange={(e) =>
                  setForm({
                    ...form,
                    intervalValue:
                      e.target.value === ""
                        ? ("" as any)
                        : parseInt(e.target.value),
                  })
                }
                onBlur={() =>
                  setForm((prev) => ({
                    ...prev,
                    intervalValue: Number(prev.intervalValue) || 1,
                  }))
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-primary focus:border-primary"
              />
              <p className="text-xs text-gray-400 mt-1">
                e.g. 3 with Daily = every 3 days
              </p>
            </div>
          </div>

          {/* Days of week (shown when WEEKLY) */}
          {form.intervalType === "WEEKLY" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Days of Week
              </label>
              <div className="flex flex-wrap gap-2">
                {daysOfWeekOptions.map((d) => (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => toggleDayOfWeek(d.value)}
                    className={`px-3 py-1 rounded-full text-sm border transition ${
                      form.daysOfWeek.includes(d.value)
                        ? "bg-primary text-white border-primary"
                        : "bg-white text-gray-600 border-gray-300 hover:border-primary"
                    }`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Day of month (shown when MONTHLY) */}
          {form.intervalType === "MONTHLY" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Day of Month
              </label>
              <input
                type="number"
                min={1}
                max={31}
                value={form.dayOfMonth}
                onChange={(e) =>
                  setForm({
                    ...form,
                    dayOfMonth:
                      e.target.value === ""
                        ? ("" as any)
                        : parseInt(e.target.value),
                  })
                }
                onBlur={() =>
                  setForm((prev) => {
                    const val = Number(prev.dayOfMonth) || 1;
                    return {
                      ...prev,
                      dayOfMonth: Math.min(31, Math.max(1, val)),
                    };
                  })
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-primary focus:border-primary"
              />
            </div>
          )}

          {/* Start Date & Duration row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="relative" ref={calendarRef}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <button
                type="button"
                onClick={() => {
                  setShowCalendar(!showCalendar);
                  if (!showCalendar) {
                    const [y, m] = form.startDate.split("-").map(Number);
                    setCalendarViewDate({ month: m - 1, year: y });
                  }
                }}
                className="w-full flex items-center justify-between border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white hover:border-primary focus:ring-primary focus:border-primary transition"
              >
                <span
                  className={form.startDate ? "text-gray-900" : "text-gray-400"}
                >
                  {form.startDate
                    ? new Date(form.startDate + "T00:00:00").toLocaleDateString(
                        "en-US",
                        {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        },
                      )
                    : "Select date"}
                </span>
                <CalendarDays className="w-4 h-4 text-gray-400" />
              </button>

              {showCalendar &&
                (() => {
                  const { month, year } = calendarViewDate;
                  const firstDay = new Date(year, month, 1).getDay();
                  const daysInMonth = new Date(year, month + 1, 0).getDate();
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const selectedParts = form.startDate.split("-").map(Number);

                  const monthNames = [
                    "January",
                    "February",
                    "March",
                    "April",
                    "May",
                    "June",
                    "July",
                    "August",
                    "September",
                    "October",
                    "November",
                    "December",
                  ];

                  const prevMonth = () => {
                    setCalendarViewDate((prev) =>
                      prev.month === 0
                        ? { month: 11, year: prev.year - 1 }
                        : { month: prev.month - 1, year: prev.year },
                    );
                  };
                  const nextMonth = () => {
                    setCalendarViewDate((prev) =>
                      prev.month === 11
                        ? { month: 0, year: prev.year + 1 }
                        : { month: prev.month + 1, year: prev.year },
                    );
                  };

                  return (
                    <div className="absolute z-50 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-3 w-72">
                      {/* Month/Year header */}
                      <div className="flex items-center justify-between mb-2">
                        <button
                          type="button"
                          onClick={prevMonth}
                          className="p-1 hover:bg-gray-100 rounded-md transition"
                        >
                          <ChevronLeft className="w-4 h-4 text-gray-600" />
                        </button>
                        <div className="flex items-center gap-1">
                          <select
                            value={month}
                            onChange={(e) =>
                              setCalendarViewDate((prev) => ({
                                ...prev,
                                month: Number(e.target.value),
                              }))
                            }
                            className="text-sm font-semibold text-gray-800 bg-transparent cursor-pointer hover:text-primary focus:outline-none appearance-none pr-1"
                          >
                            {monthNames.map((m, i) => (
                              <option key={m} value={i}>
                                {m}
                              </option>
                            ))}
                          </select>
                          <select
                            value={year}
                            onChange={(e) =>
                              setCalendarViewDate((prev) => ({
                                ...prev,
                                year: Number(e.target.value),
                              }))
                            }
                            className="text-sm font-semibold text-gray-800 bg-transparent cursor-pointer hover:text-primary focus:outline-none appearance-none"
                          >
                            {Array.from(
                              { length: 10 },
                              (_, i) => new Date().getFullYear() + i,
                            ).map((y) => (
                              <option key={y} value={y}>
                                {y}
                              </option>
                            ))}
                          </select>
                        </div>
                        <button
                          type="button"
                          onClick={nextMonth}
                          className="p-1 hover:bg-gray-100 rounded-md transition"
                        >
                          <ChevronRight className="w-4 h-4 text-gray-600" />
                        </button>
                      </div>

                      {/* Day-of-week headers */}
                      <div className="grid grid-cols-7 text-center text-xs text-gray-400 mb-1">
                        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
                          <span key={d} className="py-1">
                            {d}
                          </span>
                        ))}
                      </div>

                      {/* Day grid */}
                      <div className="grid grid-cols-7 text-center text-sm">
                        {Array.from({ length: firstDay }).map((_, i) => (
                          <span key={`empty-${i}`} />
                        ))}
                        {Array.from({ length: daysInMonth }).map((_, i) => {
                          const day = i + 1;
                          const dateObj = new Date(year, month, day);
                          const isSelected =
                            selectedParts[0] === year &&
                            selectedParts[1] === month + 1 &&
                            selectedParts[2] === day;
                          const isToday = dateObj.getTime() === today.getTime();
                          const isPast = dateObj < today;

                          return (
                            <button
                              key={day}
                              type="button"
                              disabled={isPast}
                              onClick={() => {
                                const mm = String(month + 1).padStart(2, "0");
                                const dd = String(day).padStart(2, "0");
                                setForm({
                                  ...form,
                                  startDate: `${year}-${mm}-${dd}`,
                                });
                                setShowCalendar(false);
                              }}
                              className={`py-1.5 rounded-lg transition text-sm ${
                                isSelected
                                  ? "bg-primary text-white font-semibold"
                                  : isToday
                                    ? "bg-primary/10 text-primary font-medium"
                                    : isPast
                                      ? "text-gray-300 cursor-not-allowed"
                                      : "text-gray-700 hover:bg-gray-100"
                              }`}
                            >
                              {day}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Duration (days)
              </label>
              <input
                type="number"
                min={1}
                value={form.durationDays}
                onChange={(e) =>
                  setForm({
                    ...form,
                    durationDays:
                      e.target.value === ""
                        ? ("" as any)
                        : parseInt(e.target.value),
                  })
                }
                onBlur={() =>
                  setForm((prev) => ({
                    ...prev,
                    durationDays: Number(prev.durationDays) || 1,
                  }))
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-primary focus:border-primary"
              />
            </div>
          </div>

          {/* Target & Notification Type row (only for create) */}
          {!editingId && (
            <div className="grid grid-cols-2 gap-4">
              <Dropdown
                label="Target User Type"
                placeholder="Select target"
                options={targetUserTypeOptions}
                value={form.targetUserType}
                onSelect={(val) => setForm({ ...form, targetUserType: val })}
              />
              <Dropdown
                label="Notification Type"
                placeholder="Select type"
                options={notificationTypeOptions}
                value={form.notificationType}
                onSelect={(val) => setForm({ ...form, notificationType: val })}
              />
            </div>
          )}

          {/* Action buttons */}
          <div className="flex flex-col gap-3 pt-2">
            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={scheduledCreating || scheduledUpdating}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg shadow text-white text-sm ${
                  scheduledCreating || scheduledUpdating
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-primary hover:bg-primary/90"
                }`}
              >
                {scheduledCreating || scheduledUpdating ? (
                  <>
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    {editingId ? "Updating..." : "Creating..."}
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    {editingId ? "Update Schedule" : "Create Schedule"}
                  </>
                )}
              </button>

              {!editingId && (
                <div className="relative group inline-block">
                  <button
                    type="button"
                    onClick={handleDryRun}
                    disabled={
                      scheduledDryRunning || form.intervalType === "RANDOM"
                    }
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg shadow text-sm ${
                      scheduledDryRunning || form.intervalType === "RANDOM"
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-300"
                    }`}
                  >
                    {scheduledDryRunning ? (
                      <>
                        <span className="animate-spin h-4 w-4 border-2 border-amber-500 border-t-transparent rounded-full" />
                        Testing...
                      </>
                    ) : (
                      <>
                        <FlaskConical className="w-4 h-4" />
                        Dry Run
                      </>
                    )}
                  </button>
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none z-50">
                    Test a schedule configuration without actually sending
                    notifications.
                    {form.intervalType === "RANDOM" && (
                      <span className="block mt-1 text-amber-300">
                        Not available for random intervals. Send times are
                        determined by the system at runtime.
                      </span>
                    )}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-gray-900" />
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
            </div>

            {/* Dry-run results panel */}
            {scheduledDryRunResult && (
              <div className="mt-3 border border-amber-200 bg-amber-50 rounded-xl p-4 text-sm">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <span className="font-semibold text-green-700">
                    {scheduledDryRunResult.message ?? "Dry run completed"}
                  </span>
                </div>

                {scheduledDryRunResult.configuration && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 mb-3">
                    <div>
                      <p className="text-xs text-gray-500">Estimated Sends</p>
                      <p className="font-semibold text-gray-800">
                        {scheduledDryRunResult.configuration.estimatedSends ??
                          "—"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">First Send</p>
                      <p className="font-semibold text-gray-800">
                        {scheduledDryRunResult.configuration.firstSendAt
                          ? new Date(
                              scheduledDryRunResult.configuration.firstSendAt,
                            ).toLocaleString()
                          : "—"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">End Date</p>
                      <p className="font-semibold text-gray-800">
                        {scheduledDryRunResult.configuration.endDate
                          ? new Date(
                              scheduledDryRunResult.configuration.endDate,
                            ).toLocaleDateString()
                          : "—"}
                      </p>
                    </div>
                  </div>
                )}

                {scheduledDryRunResult.nextSends?.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <Info className="w-3 h-3" />
                      Upcoming scheduled sends
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {scheduledDryRunResult.nextSends.map(
                        (date: string, i: number) => (
                          <span
                            key={i}
                            className="bg-white border border-amber-200 text-amber-800 text-xs px-2 py-1 rounded-md"
                          >
                            {new Date(date).toLocaleString()}
                          </span>
                        ),
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </form>
      )}

      {/* ── Schedules List ── */}
      {scheduledLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="bg-white shadow rounded-xl p-5 border-l-4 border-l-gray-200 animate-pulse"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="h-5 bg-gray-200 rounded w-40" />
                    <div className="h-5 bg-gray-200 rounded-full w-16" />
                  </div>
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="flex gap-3">
                    <div className="h-3 bg-gray-200 rounded w-16" />
                    <div className="h-3 bg-gray-200 rounded w-24" />
                    <div className="h-3 bg-gray-200 rounded w-20" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="h-8 bg-gray-200 rounded-lg w-20" />
                  <div className="h-8 bg-gray-200 rounded-lg w-20" />
                  <div className="h-8 bg-gray-200 rounded-lg w-14" />
                  <div className="h-8 bg-gray-200 rounded-lg w-16" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : schedulesList.length === 0 ? (
        <div className="bg-white shadow rounded-2xl p-10 text-center text-gray-400">
          <CalendarDays className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p className="text-sm">No scheduled messages yet.</p>
          <p className="text-xs mt-1">
            Click "New Schedule" to create an automated message.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {schedulesList.map((item: any) => (
            <div
              key={item.id}
              className={`bg-white shadow rounded-xl p-5 border-l-4 ${
                item.isActive ? "border-l-green-500" : "border-l-gray-300"
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                {/* Left – Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-gray-900 truncate">
                      {item.title}
                    </h4>
                    <span
                      className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                        item.isActive
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {item.isActive ? "Active" : "Paused"}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                    {item.message}
                  </p>
                  <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {item.scheduleTime ?? "—"}
                    </span>
                    <span className="flex items-center gap-1">
                      <CalendarDays className="w-3.5 h-3.5" />
                      {formatIntervalLabel(item)}
                    </span>
                    {item.durationDays && (
                      <span>Duration: {item.durationDays}d</span>
                    )}
                    {item.targetUserType && (
                      <span>Target: {item.targetUserType}</span>
                    )}
                  </div>
                </div>

                {/* Right – Actions */}
                <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                  {/* Toggle */}
                  <button
                    onClick={() => handleToggle(item.id)}
                    disabled={scheduledToggling === item.id}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition border ${
                      item.isActive
                        ? "bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
                        : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100"
                    }`}
                  >
                    {scheduledToggling === item.id ? (
                      <span className="animate-spin inline-block h-3.5 w-3.5 border-2 border-current border-t-transparent rounded-full" />
                    ) : item.isActive ? (
                      <ToggleRight className="w-4 h-4" />
                    ) : (
                      <ToggleLeft className="w-4 h-4" />
                    )}
                    {item.isActive ? "Pause" : "Activate"}
                  </button>

                  {/* Send Now */}
                  <button
                    onClick={() => handleSendNow(item.id)}
                    disabled={scheduledSendingNow === item.id}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition border bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                  >
                    {scheduledSendingNow === item.id ? (
                      <span className="animate-spin inline-block h-3.5 w-3.5 border-2 border-blue-600 border-t-transparent rounded-full" />
                    ) : (
                      <PlayCircle className="w-4 h-4" />
                    )}
                    Send Now
                  </button>

                  {/* Edit */}
                  <button
                    onClick={() => handleEdit(item)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition border bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                    Edit
                  </button>

                  {/* Delete */}
                  <button
                    onClick={() => handleDelete(item.id)}
                    disabled={scheduledDeleting}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition border bg-red-50 text-red-600 border-red-200 hover:bg-red-100"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AutomatedMessages;
